/**
 * Confidence intervals for a proportion and for a Poisson count.
 *
 * Wilson's score interval rather than the textbook Wald interval (p ± z√(p(1 − p)/n)):
 * Wald collapses to zero width at 0% and 100% and undercovers badly near them, which is
 * exactly where sensitivities, specificities and prevalences live. Wilson stays inside
 * [0, 1] and keeps close to its nominal coverage at small n (Newcombe 1998).
 *
 * The Poisson interval is the exact (Garwood) one, from chi-square quantiles:
 * lower = χ²(α/2; 2x)/2, upper = χ²(1 − α/2; 2x + 2)/2.
 */

import { chiSquareQuantile, normalQuantile } from "@/lib/stats/distributions";

export interface Interval {
  lower: number;
  upper: number;
}

/** z for a two-sided interval at `level` (1.959964… for 95%). */
export function zFor(level = 0.95): number {
  return normalQuantile(1 - (1 - level) / 2);
}

export interface ProportionEstimate extends Interval {
  value: number;
  count: number;
  total: number;
}

/** x / n with its Wilson interval, or null when n is 0. */
export function wilsonInterval(
  count: number,
  total: number,
  level = 0.95
): ProportionEstimate | null {
  if (!(total > 0) || count < 0 || count > total) {
    return null;
  }
  const z = zFor(level);
  const p = count / total;
  const z2 = z * z;
  const denominator = 1 + z2 / total;
  const centre = (p + z2 / (2 * total)) / denominator;
  const half =
    (z * Math.sqrt((p * (1 - p)) / total + z2 / (4 * total * total))) /
    denominator;
  return {
    value: p,
    count,
    total,
    // Exact at the edges: the formula's rounding can leave 1e-17 where 0 belongs.
    lower: count === 0 ? 0 : Math.max(0, centre - half),
    upper: count === total ? 1 : Math.min(1, centre + half),
  };
}

/** Exact interval for a Poisson count (events, not a rate). */
export function poissonCountInterval(count: number, level = 0.95): Interval {
  const tail = (1 - level) / 2;
  return {
    lower: count === 0 ? 0 : chiSquareQuantile(tail, 2 * count) / 2,
    upper: chiSquareQuantile(1 - tail, 2 * count + 2) / 2,
  };
}
